import { Injectable, NgZone, inject } from '@angular/core';
import {
  MatSnackBar,
  MatSnackBarConfig,
  MatSnackBarRef,
} from '@angular/material/snack-bar';
import { LanguageService } from './language.service';
import { SuccessSnackBarComponent } from '../../shared/components/success-snack-bar/success-snack-bar.component';
import { ComponentType } from '@angular/cdk/overlay';
import { ErrorSnackBarComponent } from '../../shared/components/error-snack-bar/error-snack-bar.component';
import { InfoSnackBarComponent } from '../../shared/components/info-snack-bar/info-snack-bar.component';
import { HashMap } from '@jsverse/transloco';

export interface IMessage {
  title?: string;
  message: string;
}
@Injectable({
  providedIn: 'root',
})
export class SnackbarService {
  private readonly snackbar = inject(MatSnackBar);
  private readonly zone = inject(NgZone);
  private readonly languageService = inject(LanguageService);

  private readonly config: MatSnackBarConfig;

  constructor() {
    this.config = new MatSnackBarConfig();
    this.config.panelClass = ['custom-snack-bar'];
    this.config.verticalPosition = 'top';
    this.config.horizontalPosition = 'right';
    this.config.duration = 7000;
  }

  error(
    translationKey?: string,
    addLeadingErrorPrefixForTranslations = true,
    params?: HashMap,
  ) {
    this.config.panelClass = ['custom-snack-bar', 'error'];
    const translationErrorPrefix = addLeadingErrorPrefixForTranslations
      ? 'errors.'
      : '';
    const message = this.generateSnackBarMessage(
      translationErrorPrefix + translationKey,
      params,
    );
    this.showFromComponent({ message: message }, ErrorSnackBarComponent);
  }

  errorMessage(message: string) {
    this.config.panelClass = ['custom-snack-bar', 'error'];
    this.showFromComponent({ message: message }, ErrorSnackBarComponent);
  }

  success(translationKey: string, params?: HashMap): void {
    this.config.panelClass = ['custom-snack-bar', 'success'];
    const message = this.generateSnackBarTitleAndMessage(
      translationKey,
      params,
    );
    this.showFromComponent(message, SuccessSnackBarComponent);
  }

  progress(
    translationKey: string,
    params?: HashMap,
  ): MatSnackBarRef<InfoSnackBarComponent> {
    const message = this.generateSnackBarMessage(translationKey, params);
    return this.showFromComponent({ message }, InfoSnackBarComponent, {
      panelClass: ['custom-snack-bar', 'info'],
      duration: 0,
    });
  }

  warning(translationKey: string) {
    this.config.panelClass = ['custom-snack-bar', 'warning'];
    const message = this.generateSnackBarMessage(translationKey);
    this.show(message);
  }

  info(translationKey: string) {
    this.config.panelClass = ['custom-snack-bar', 'info'];
    const message = this.generateSnackBarMessage(translationKey);
    this.show(message);
  }

  private generateSnackBarMessage(
    translationKey: string,
    params?: HashMap,
  ): string {
    return this.languageService.translateOnRuntime(translationKey, params);
  }

  private generateSnackBarTitleAndMessage(
    translationKey: string,
    params?: HashMap,
  ): IMessage {
    return {
      title: this.languageService.translateOnRuntime(
        translationKey + '.title',
        params,
      ),
      message: this.languageService.translateOnRuntime(
        translationKey + '.message',
        params,
      ),
    };
  }

  private show(message: string, config?: MatSnackBarConfig) {
    config = config ?? this.config;
    this.zone.run(() => {
      this.snackbar.open(message, 'x', config);
    });
  }

  private showFromComponent<TComponent>(
    message: IMessage,
    component: ComponentType<TComponent>,
    config?: MatSnackBarConfig,
  ): MatSnackBarRef<TComponent> {
    const mergedConfig: MatSnackBarConfig = {
      ...this.config,
      ...config,
      data: {
        ...message,
      },
    };
    return this.zone.run(() =>
      this.snackbar.openFromComponent(component, mergedConfig),
    );
  }
}
